import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { chatKeys, feedKeys, workFileKeys, workBoardKeys, filesOverviewKeys, attentionKeys } from '~/lib/query-keys';
import { documentKeys } from '~/hooks/queries/use-documents';
import type { ChatThreadRow } from '~/hooks/queries/use-chats';
import type { ChatScopeEntity, ChatScopeResult } from '~/hooks/queries/use-chat-scope';
import type { AttachedToContainer } from './use-document-mutations';

/**
 * Restore a chat out of the Recycle bin (FRONT-527). A trashed thread goes back
 * to `archived` — alive and reopenable again, just not pinned as an active pill.
 * The inverse of the trash action; clears nothing else. Invalidating the threads
 * root refreshes both the Recycle bin and the Open list.
 */
export function useRestoreChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.patch<{ thread: ChatThreadRow }>(
        `/api/chats/${encodeURIComponent(sessionId)}`,
        { state: 'archived' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
    },
  });
}

/**
 * Move a chat to the Recycle bin (FRONT-523, the resolution bar's red action).
 * `state=trashed` drops it out of the active/Open lists into the Recycle bin
 * (30-day retention, restorable via {@link useRestoreChat}); `resolution=trash`
 * records the user's verdict for the telemetry signal. Reversible — purge is a
 * separate, later sweep.
 */
export function useTrashChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.patch<{ thread: ChatThreadRow }>(
        `/api/chats/${encodeURIComponent(sessionId)}`,
        { state: 'trashed', resolution: 'trash' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
    },
  });
}

/**
 * Resolve a chat to the knowledge graph (FRONT-523). Per Mattias's decision the
 * thread is marked resolved-to-graph on the click (the user's declared intent);
 * the actual fact extraction still flows through the existing propose/preview
 * gate the modal triggers alongside this. Full graph→chat provenance wiring is
 * the provenance child ticket. FRONT-1309: this used to also leave the active
 * pill row (`state: 'resolved_graph'`) — keeping is orthogonal to liveness
 * now, so `resolution` alone is sent and the chat's liveness is untouched
 * (still active if it was, and a later message revives it either way).
 */
export function useResolveChatToGraph() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.patch<{ thread: ChatThreadRow }>(
        `/api/chats/${encodeURIComponent(sessionId)}`,
        { resolution: 'graph' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
    },
  });
}

/**
 * Permanently delete trashed chats (FRONT-594) — the "delete permanently"
 * button in the Recycle bin. POSTs the session ids; the server purges only the
 * caller's own trashed threads (redact → hard-delete messages → tombstone).
 * Irreversible. Invalidating the threads root refreshes the bin.
 */
export function usePurgeChats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionIds: string[]) =>
      api.post<{ affected: number }>(`/api/chats/purge`, { sessionIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
    },
  });
}

/**
 * Move several chats to the Recycle bin at once (FRONT-594, bulk-trash on the
 * Chats Open list). One call to the bulk endpoint (FRONT-608) — the server flips
 * every selected chat to `trashed` in a single owner-scoped update, replacing
 * the old one-PATCH-per-chat fan-out that scaled linearly with the selection.
 */
export function useBulkTrashChats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionIds: string[]) =>
      api.post<{ affected: number }>(`/api/chats/bulk`, {
        action: 'trash',
        sessionIds,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
    },
  });
}

/**
 * Restore several chats from the Recycle bin at once (FRONT-594). One bulk call
 * (FRONT-608) returns every selected chat to `archived` — alive and reopenable.
 */
export function useBulkRestoreChats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionIds: string[]) =>
      api.post<{ affected: number }>(`/api/chats/bulk`, {
        action: 'restore',
        sessionIds,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
    },
  });
}

export interface PinChatInput {
  sessionId: string;
  pinned: boolean;
}

/**
 * Pin or unpin a chat (FRONT-2433) — the Chats page's pinned strip. A pinned
 * chat is also kept out of the 14-day auto-file sweep server-side. Not
 * optimistic: the list refetches from the server's answer.
 */
export function usePinChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, pinned }: PinChatInput) =>
      api.patch<{ thread: ChatThreadRow }>(
        `/api/chats/${encodeURIComponent(sessionId)}`,
        { pinned },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
    },
  });
}

export interface RenameChatInput {
  sessionId: string;
  /** The new chat name. Trimmed + length-validated server-side. */
  title: string;
}

/**
 * Rename a chat inline (FRONT-545). PATCHes just the title — no state change —
 * and invalidates the threads root so the new name shows up everywhere the title
 * is rendered (the home pills, the Chats page Open + Recycle bin cards, search
 * results, the Files "From chat" badge). The server overwrites unconditionally,
 * so this is the user's deliberate rename winning over the auto-minted title.
 */
export function useRenameChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, title }: RenameChatInput) =>
      api.patch<{ thread: ChatThreadRow }>(
        `/api/chats/${encodeURIComponent(sessionId)}`,
        { title },
      ),
    onSuccess: (data, { sessionId }) => {
      // FRONT-3152: the chat window's header reads this one row, so patch its
      // title in place first — otherwise the old name shows again for the
      // length of the refetch after the edit field closes. Only the title is
      // copied: the PATCH echo is not the detail read's full shape.
      qc.setQueryData<ChatThreadRow | null>(chatKeys.thread(sessionId), (old) =>
        old ? { ...old, title: data.thread.title } : old,
      );
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
    },
  });
}

/**
 * Ask Eddy for a short title read from the conversation so far — the wizard
 * beside the chat window's title. It only suggests; the caller saves it with
 * {@link useRenameChat}, so an AI title and a typed one take the same path.
 */
export function useSuggestChatTitle() {
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<{ title: string }>(`/api/chats/${encodeURIComponent(sessionId)}/suggest-title`, {}),
  });
}

export interface ResolveChatToDocumentInput {
  sessionId: string;
  /** The serialized conversation markdown to save as the document body. */
  content: string;
  /** Optional title; the server falls back to the thread title. */
  title?: string;
}

export interface ResolveChatToDocumentResult {
  filename: string;
  thread: ChatThreadRow;
  attachedTo: AttachedToContainer | null;
}

/**
 * Resolve a chat to a Document (FRONT-523, the resolution bar's keep action).
 * The server saves the conversation as a memory document, wires the
 * chat↔document backlink, and stamps resolution=document + keptAt — its
 * document shows a "From chat" badge in Files. `state`/liveness is untouched
 * since FRONT-1309 (the chat stays a pill if it already was one). Invalidates
 * the threads root (covers the Files backlink map), the document list, and
 * the inbox feed so all three surfaces pick up the new document.
 *
 * FRONT-1737: when the chat is scoped to a Work container, the backend
 * files the document there automatically and reports it back as
 * `attachedTo` — invalidate that container's own attachment list AND
 * `workBoardKeys.all` (the board card's `· N files` chip), same two keys a
 * manual attach invalidates (see use-work-file-mutations.ts's docblock for
 * why the board key is load-bearing), or the project page and board card
 * go stale.
 */
export function useResolveChatToDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, content, title }: ResolveChatToDocumentInput) =>
      api.post<ResolveChatToDocumentResult>(
        `/api/chats/${encodeURIComponent(sessionId)}/resolve-document`,
        { content, title },
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
      qc.invalidateQueries({ queryKey: documentKeys.all });
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      qc.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-2392: see use-files.ts's matching comment.
      qc.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      if (data.attachedTo) {
        qc.invalidateQueries({ queryKey: workFileKeys.byContainer(data.attachedTo.entityId) });
        qc.invalidateQueries({ queryKey: workBoardKeys.all });
      }
    },
  });
}

/** Human-readable message for a failed chat-scope change (FRONT-1738). A
 *  409 means the target project is on the Inactive view — the backend's
 *  write freeze on an archived container — never shown as a raw status
 *  code. Mirrors `use-container-context.ts`'s `containerErrorMessage`. */
function chatScopeErrorMessage(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  if (status === 409) return 'That project is inactive — reactivate it before filing a chat there.';
  if (status === 404) return "That project or chat couldn't be found.";
  if (status === 400) return "That isn't something a chat can be filed under.";
  if (err instanceof Error && err.message) return err.message.slice(0, 200);
  return 'Failed to move this chat';
}

export interface SetChatScopeInput {
  sessionId: string;
  /** The target container id, or `null` to clear the chat's scope entirely. */
  entityId: string | null;
}

/**
 * File, move, or clear a chat's Work-container scope (FRONT-1738) — the
 * `ChatScopeChip`'s picker and its "Remove from project"/"Add to project"
 * rows all funnel through this one mutation.
 * `PUT /api/chat/sessions/:sessionId/scope`, `{ entityId }`. The response
 * carries the SAME `{ entity }` envelope the GET route returns — wrapped as
 * a `known: true` `ChatScopeResult` (`use-chat-scope.ts`) and written
 * straight into `useChatScope`'s own cache entry so the chip's next render
 * reflects the move with no extra round trip. FRONT-1813 finding 1: this
 * mutation always gets a genuine completed response (never a 404 — a scope
 * PUT against a chat with no owned row 404s at the HTTP layer and lands in
 * `onError` below, not here), so it is always safe to mark `known: true`.
 * Also invalidates `chatKeys.threadsRoot()` — the Chats-overview project
 * label reads the same `ChatThreadRow.scope` field, and
 * `chatKeys.byContainer(...)` nests under this same prefix (see
 * `query-keys.ts`), so this ONE invalidation already reaches the container
 * this chat just left and the one it just joined; no separate by-container
 * call needed.
 */
export function useSetChatScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, entityId }: SetChatScopeInput) =>
      api.put<{ entity: ChatScopeEntity | null }>(
        `/api/chat/sessions/${encodeURIComponent(sessionId)}/scope`,
        { entityId },
      ),
    onSuccess: (data, variables) => {
      const result: ChatScopeResult = { known: true, entity: data.entity };
      qc.setQueryData(chatKeys.scope(variables.sessionId), result);
      qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
    },
    onError: (err) => toast.error(chatScopeErrorMessage(err)),
  });
}
